import { Components, Theme } from '@mui/material/styles'

/**
 * ## `text.primary`, not `primary.contrastText`
 *
 * `contrastText` is the colour legible ON the primary colour, i.e. on a
 * primary-FILLED surface. A list row is not on one — a `MuiList` renders inside a
 * white Paper (a menu popover, a bottom-sheet picker, a legal document's bullets).
 * `primary.main` defaults to BLACK (`./palette.ts`), so MUI computes `contrastText`
 * as `#fff` and both declarations below painted white on white: the row's TEXT, and
 * the `.aboutList` bullet's BACKGROUND (an invisible dot).
 *
 * ## Reach, stated precisely — this is the one in the sweep with real breadth
 *
 * `& li` is not scoped to `ListItem`: a MUI `MenuItem` IS an `li`, so this reaches
 * every `<Menu>` and every `<Select>`'s option list as well as every `<List>`.
 * Worse, `.<emotionClass> li` is specificity 0-1-1, which BEATS the 0-1-0 of a
 * plain `sx` on the row — so a call site cannot simply override it and may not
 * realise it failed.
 *
 * Two groups of live consumers:
 *
 * - **Protected already.** Menus routed through `getPickerMenuProps`
 *   (`components/UI/pickerListStyle.ts`) style rows via `& .MuiMenuItem-root`,
 *   which is 0-2-0 and wins. That covers `SortField` and `MoreOptionsMenu`'s
 *   desktop branch. `MuiTelInput-MenuItem` below carries its own `!important`.
 * - **NOT protected.** Bare `<List>` + `ListItem` — `Fields/SelectDrawer`,
 *   `pages/profile/EditFieldModal` (×2), `pages/profile/UpdatePhoto`,
 *   `UI/LegalDocumentDrawer`'s markdown `ul`. There the theme wins on specificity.
 *
 * **Honest limit of the evidence.** I could not get any of those five on screen in
 * the session that made this change (no valid customer session against the shared
 * API; this worktree's own API does not compile, for an unrelated pre-existing
 * reason). Every surface I COULD drive — the certificates list and its keep-mounted
 * drawers, the public certificate page, the access gate, `/create-account`,
 * `/sign-in` — reported `.MuiList-root li` = 0, so I am NOT claiming a screen is
 * currently broken. The declaration is wrong on its own terms and the specificity
 * makes it hard to escape; that is sufficient reason to fix it, and it is a
 * different claim from "users see this".
 */
export const MuiList: Components<Theme>['MuiList'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      padding: '0',
      borderRadius: 0,

      li: {
        fontSize: '12px',
        lineHeight: '16px',
        color: theme.palette.text.primary,
        position: 'relative',
        listStyle: 'none',

        // `&.selectMenu` was removed here (2026-08-23). It had exactly ONE consumer —
        // the MenuItems in `components/Fields/SelectField.tsx` — so it was a private
        // style for one component that happened to live in a global theme override,
        // and that placement was the problem: `.MuiList-root li.selectMenu` is two
        // classes plus an element, which out-specifies any sx that component could
        // write, so its 8px padding and tenant-derived radius could not be overridden
        // from the call site at all. That dropdown now takes the house menu chrome
        // (`MENU_PAPER_SX` / `MENU_ITEM_SX`) like every other menu in both apps.

        '&.MuiTelInput-MenuItem': {
          margin: 0,
          padding: '0 6px',
          color: `${theme.palette.text.primary} !important`,
          fontSize: '16px',
          '& *': { color: `${theme.palette.text.primary} !important`, fontSize: '16px' },
        },
      },

      '&.aboutList': {
        li: {
          padding: '0 0 0 20px',
          '&:before': {
            position: 'absolute',
            left: '7px',
            top: '6px',
            width: '4px',
            height: '4px',
            borderRadius: `${theme.shape.borderRadius / 2}px`,
            content: '""',
            // The bullet DOT. `contrastText` here made it white-on-white, i.e. a
            // bulleted list with no bullets. `.aboutList` has no consumer anywhere in
            // `apps/app/src` today, so this one is purely latent — corrected rather
            // than deleted because the block is otherwise intact and a future
            // consumer would inherit the same invisible dot.
            background: theme.palette.text.primary,
          },
        },
      },
    }),
  },
}
